import React from "react";

/**
 * SummaryCard (Global/Minimal)
 * - รองรับไอคอน (React element) ผ่าน prop `icon`
 * - รองรับโทนสีผ่าน `variant`: 'indigo' | 'yellow' | 'green' | 'red' | 'gray'
 * - คลิกทั้งการ์ดได้: ส่ง `onClick` หรือ `href`
 * - ยังรองรับการใช้ `bgColor` / `textColor` แบบเดิม (fallback)
 */
const tone = {
  indigo: {
    wrap: "bg-indigo-50 border-indigo-200 dark:bg-indigo-900/30 dark:border-indigo-700",
    icon: "text-indigo-600 dark:text-indigo-300",
    title: "text-indigo-700 dark:text-indigo-200",
    value: "text-indigo-800 dark:text-indigo-300",
    ring: "focus:ring-indigo-300 dark:focus:ring-indigo-600",
  },
  yellow: {
    wrap: "bg-yellow-50 border-yellow-200 dark:bg-yellow-900/30 dark:border-yellow-700",
    icon: "text-yellow-600 dark:text-yellow-300",
    title: "text-yellow-700 dark:text-yellow-200",
    value: "text-yellow-800 dark:text-yellow-300",
    ring: "focus:ring-yellow-300 dark:focus:ring-yellow-500",
  },
  green: {
    wrap: "bg-green-50 border-green-200 dark:bg-green-900/30 dark:border-green-700",
    icon: "text-green-600 dark:text-green-300",
    title: "text-green-700 dark:text-green-200",
    value: "text-green-800 dark:text-green-300",
    ring: "focus:ring-green-300 dark:focus:ring-green-600",
  },
  red: {
    wrap: "bg-red-50 border-red-200 dark:bg-red-900/30 dark:border-red-700",
    icon: "text-red-600 dark:text-red-300",
    title: "text-red-700 dark:text-red-200",
    value: "text-red-800 dark:text-red-300",
    ring: "focus:ring-red-300 dark:focus:ring-red-600",
  },
  gray: {
    wrap: "bg-gray-50 border-gray-200 dark:bg-gray-800/40 dark:border-gray-700",
    icon: "text-gray-600 dark:text-gray-300",
    title: "text-gray-700 dark:text-gray-200",
    value: "text-gray-800 dark:text-gray-300",
    ring: "focus:ring-gray-300 dark:focus:ring-gray-600",
  },
};

const SummaryCard = ({
  title,
  value,
  subtext, // (optional)
  icon, // (optional)
  variant = "indigo",
  onClick, // (optional)
  href, // (optional)
  className = "",
  // legacy props (ยังรองรับ)
  bgColor,
  textColor,
}) => {
  const t = tone[variant] || tone.indigo;

  const wrapColor = bgColor ? `${bgColor} border-transparent` : t.wrap;
  const textTitle = textColor ? textColor : t.title;
  const textValue = textColor ? textColor : t.value;
  const iconColor = textColor ? textColor : t.icon;
  const focusRing = t.ring;

  const Wrapper = href ? "a" : "div";
  const wrapperProps = href
    ? { href }
    : onClick
    ? { role: "button", onClick }
    : {};

  return (
    <Wrapper
      {...wrapperProps}
      className={[
        "group relative overflow-hidden rounded-lg border p-4",
        "shadow-sm hover:shadow-md transition-all duration-300",
        "focus:outline-none focus:ring-2", focusRing,
        "bg-white/70 dark:bg-gray-900/60 backdrop-blur",
        wrapColor,
        onClick || href ? "cursor-pointer" : "",
        className,
      ].join(" ")}
    >
      <div className="flex items-center gap-3">
        {icon && (
          <div
            className={`h-10 w-10 grid place-items-center rounded-md bg-white/60 dark:bg-gray-800/70 ${iconColor}`}
          >
            {icon}
          </div>
        )}
        <div className="min-w-0">
          <h2 className={`text-sm font-medium truncate ${textTitle}`}>
            {title}
          </h2>
          <div className="flex items-baseline gap-2">
            <p className={`text-2xl font-bold ${textValue}`}>{value}</p>
            {subtext && (
              <span className="text-xs text-gray-500 dark:text-gray-400 truncate">
                {subtext}
              </span>
            )}
          </div>
        </div>
      </div>
    </Wrapper>
  );
};

export default SummaryCard;
